import { logger } from '../../utils/logger';
import {
  Context,
  ContextItem,
  ContextType,
  CompressionStrategy
} from './types';

// 压缩配置
export interface CompressorConfig {
  strategy?: CompressionStrategy;
  targetRatio?: number;         // 目标压缩比例 (0-1)
  minItemSize?: number;         // 最小项目大小（字节）
  preserveStructure?: boolean;  // 保留结构信息
  customStrategies?: Map<ContextType, CompressionStrategy>;
}

// 压缩结果
export interface CompressionResult {
  compressed: boolean;
  originalSize: number;
  compressedSize: number;
  ratio: number;
  strategy: CompressionStrategy;
  metadata?: Record<string, any>;
}

// 上下文压缩器
export class ContextCompressor {
  private config: Required<CompressorConfig>;
  
  constructor(config: CompressorConfig = {}) {
    this.config = {
      strategy: CompressionStrategy.HYBRID,
      targetRatio: 0.5,
      minItemSize: 100,
      preserveStructure: true,
      customStrategies: new Map(),
      ...config
    };
  }
  
  /**
   * 压缩上下文
   */
  async compress(context: Context): Promise<Context> {
    const startTime = Date.now();
    logger.debug('Compressing context:', {
      id: context.id,
      originalSize: context.totalSize
    });
    
    try {
      const compressedItems: ContextItem[] = [];
      let totalCompressed = 0;
      
      for (const item of context.items) {
        // 跳过小项目
        if ((item.size || 0) < this.config.minItemSize) {
          compressedItems.push(item);
          continue;
        }
        
        // 获取压缩策略
        const strategy = this.getStrategyForItem(item);
        
        // 压缩项目
        const result = await this.compressItem(item, strategy);
        
        if (result.compressed) {
          compressedItems.push({
            ...item,
            content: result.content,
            size: result.compressedSize,
            metadata: {
              ...item.metadata,
              compression: {
                strategy,
                originalSize: result.originalSize,
                ratio: result.ratio
              }
            }
          });
          totalCompressed++;
        } else {
          compressedItems.push(item);
        }
      }
      
      const compressedContext: Context = {
        ...context,
        items: compressedItems,
        totalSize: this.calculateTotalSize(compressedItems),
        metadata: {
          ...context.metadata,
          compressed: true,
          compressionStats: {
            itemsCompressed: totalCompressed,
            originalSize: context.totalSize,
            compressedSize: this.calculateTotalSize(compressedItems),
            ratio: this.calculateTotalSize(compressedItems) / context.totalSize
          }
        }
      };
      
      const duration = Date.now() - startTime;
      logger.info(`Context compressed in ${duration}ms:`, {
        itemsCompressed: totalCompressed,
        originalSize: context.totalSize,
        compressedSize: compressedContext.totalSize,
        ratio: `${Math.round((1 - compressedContext.totalSize / context.totalSize) * 100)}%`
      });
      
      return compressedContext;
      
    } catch (error) {
      logger.error('Failed to compress context:', error);
      return context;
    }
  }
  
  /**
   * 解压上下文
   */
  async decompress(context: Context): Promise<Context> {
    if (!context.metadata.compressed) {
      return context;
    }
    
    const decompressedItems: ContextItem[] = [];
    
    for (const item of context.items) {
      if (item.metadata?.compression) {
        // 这里应该实现解压逻辑
        // 目前简单返回原始项目
        decompressedItems.push(item);
      } else {
        decompressedItems.push(item);
      }
    }
    
    return {
      ...context,
      items: decompressedItems,
      totalSize: this.calculateTotalSize(decompressedItems),
      metadata: {
        ...context.metadata,
        compressed: false
      }
    };
  }
  
  /**
   * 压缩单个项目
   */
  private async compressItem(
    item: ContextItem,
    strategy: CompressionStrategy
  ): Promise<{
    compressed: boolean;
    content: any;
    originalSize: number;
    compressedSize: number;
    ratio: number;
  }> {
    const originalSize = item.size || 0;
    let compressedContent: any;
    
    switch (strategy) {
      case CompressionStrategy.SUMMARY:
        compressedContent = await this.summarizeContent(item);
        break;
      case CompressionStrategy.TRUNCATE:
        compressedContent = await this.truncateContent(item);
        break;
      case CompressionStrategy.SAMPLE:
        compressedContent = await this.sampleContent(item);
        break;
      case CompressionStrategy.SEMANTIC:
        compressedContent = await this.semanticCompress(item);
        break;
      default:
        compressedContent = item.content;
    }
    
    const compressedSize = JSON.stringify(compressedContent).length;
    const ratio = compressedSize / originalSize;
    
    // 只有在达到目标压缩比时才压缩
    if (ratio <= this.config.targetRatio) {
      return {
        compressed: true,
        content: compressedContent,
        originalSize,
        compressedSize,
        ratio
      };
    }
    
    return {
      compressed: false,
      content: item.content,
      originalSize,
      compressedSize: originalSize,
      ratio: 1
    };
  }
  
  /**
   * 摘要压缩
   */
  private async summarizeContent(item: ContextItem): Promise<any> {
    const content = item.content;
    
    switch (item.type) {
      case ContextType.KNOWLEDGE:
        return this.summarizeKnowledge(content);
      case ContextType.HISTORY:
        return this.summarizeHistory(content);
      case ContextType.TOOL:
        return this.summarizeTool(content);
      default:
        return this.genericSummarize(content);
    }
  }
  
  /**
   * 截断压缩
   */
  private async truncateContent(item: ContextItem): Promise<any> {
    const content = item.content;
    const maxLength = Math.floor((item.size || 0) * this.config.targetRatio);
    
    if (typeof content === 'string') {
      return content.substring(0, maxLength) + '...';
    }
    
    if (Array.isArray(content)) {
      const itemsToKeep = Math.floor(content.length * this.config.targetRatio);
      return content.slice(0, itemsToKeep);
    }
    
    if (typeof content === 'object') {
      const keys = Object.keys(content);
      const keysToKeep = Math.floor(keys.length * this.config.targetRatio);
      const keptKeys = keys.slice(0, keysToKeep);
      
      const truncated: any = {};
      for (const key of keptKeys) {
        truncated[key] = content[key];
      }
      return truncated;
    }
    
    return content;
  }
  
  /**
   * 采样压缩
   */
  private async sampleContent(item: ContextItem): Promise<any> {
    const content = item.content;
    
    if (Array.isArray(content)) {
      // 均匀采样
      const sampleSize = Math.floor(content.length * this.config.targetRatio);
      const step = Math.floor(content.length / sampleSize);
      const sampled = [];
      
      for (let i = 0; i < content.length; i += step) {
        sampled.push(content[i]);
      }
      
      return sampled;
    }
    
    // 对于其他类型，回退到截断
    return this.truncateContent(item);
  }
  
  /**
   * 语义压缩
   */
  private async semanticCompress(item: ContextItem): Promise<any> {
    // 这是一个高级功能，需要 AI 模型支持
    // 目前使用简单的结构保留策略
    
    const content = item.content;
    
    if (typeof content !== 'object' || content === null) {
      return this.truncateContent(item);
    }
    
    // 提取关键信息
    const compressed = this.extractKeyInfo(content, item.type);
    
    return compressed;
  }
  
  /**
   * 压缩知识内容
   */
  private summarizeKnowledge(content: any): any {
    if (!content || typeof content !== 'object') {
      return content;
    }
    
    return {
      id: content.id,
      title: content.title,
      summary: this.truncateString(content.content || '', 200),
      category: content.category,
      tags: content.tags?.slice(0, 5),
      score: content.score
    };
  }
  
  /**
   * 压缩历史内容
   */
  private summarizeHistory(content: any): any {
    if (Array.isArray(content)) {
      // 保留最近的几条
      return content.slice(-5).map(entry => ({
        role: entry.role,
        summary: this.truncateString(entry.content || '', 100),
        timestamp: entry.timestamp
      }));
    }
    
    return {
      role: content.role,
      summary: this.truncateString(content.content || '', 100),
      timestamp: content.timestamp
    };
  }
  
  /**
   * 压缩工具内容
   */
  private summarizeTool(content: any): any {
    if (!content || typeof content !== 'object') {
      return content;
    }
    
    return {
      toolName: content.toolName,
      parameters: this.compactObject(content.parameters),
      success: !content.error,
      summary: content.error 
        ? `Error: ${content.error.message}`
        : this.truncateString(JSON.stringify(content.result), 100)
    };
  }
  
  /**
   * 通用摘要
   */
  private genericSummarize(content: any): any {
    if (typeof content === 'string') {
      return this.truncateString(content, 200);
    }
    
    if (Array.isArray(content)) {
      return content.slice(0, 5).map(item => 
        typeof item === 'object' ? this.compactObject(item) : item
      );
    }
    
    if (typeof content === 'object' && content !== null) {
      return this.compactObject(content);
    }
    
    return content;
  }
  
  /**
   * 提取关键信息
   */
  private extractKeyInfo(obj: any, type: ContextType): any {
    const keyFields: Record<ContextType, string[]> = {
      [ContextType.SYSTEM]: ['prompt', 'role', 'constraints'],
      [ContextType.USER]: ['query', 'intent', 'context'],
      [ContextType.HISTORY]: ['role', 'content', 'timestamp'],
      [ContextType.KNOWLEDGE]: ['title', 'category', 'tags', 'score'],
      [ContextType.WORKFLOW]: ['name', 'status', 'currentStep'],
      [ContextType.TOOL]: ['toolName', 'parameters', 'result'],
      [ContextType.STATE]: ['status', 'variables', 'timestamp'],
      [ContextType.METADATA]: ['id', 'type', 'source']
    };
    
    const fields = keyFields[type] || [];
    const result: any = {};
    
    for (const field of fields) {
      if (field in obj) {
        result[field] = obj[field];
      }
    }
    
    // 添加任何其他重要字段
    const importantFields = ['id', 'name', 'type', 'status', 'error'];
    for (const field of importantFields) {
      if (field in obj && !(field in result)) {
        result[field] = obj[field];
      }
    }
    
    return result;
  }
  
  /**
   * 压缩对象
   */
  private compactObject(obj: any, maxDepth: number = 2): any {
    if (maxDepth <= 0 || typeof obj !== 'object' || obj === null) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.slice(0, 3).map(item => 
        this.compactObject(item, maxDepth - 1)
      );
    }
    
    const result: any = {};
    const keys = Object.keys(obj).slice(0, 5);
    
    for (const key of keys) {
      result[key] = this.compactObject(obj[key], maxDepth - 1);
    }
    
    return result;
  }
  
  /**
   * 截断字符串
   */
  private truncateString(str: string, maxLength: number): string {
    if (str.length <= maxLength) {
      return str;
    }
    
    return str.substring(0, maxLength - 3) + '...';
  }
  
  /**
   * 获取项目的压缩策略
   */
  private getStrategyForItem(item: ContextItem): CompressionStrategy {
    // 检查自定义策略
    const customStrategy = this.config.customStrategies.get(item.type);
    if (customStrategy) {
      return customStrategy;
    }
    
    // 默认策略
    const defaultStrategies: Record<ContextType, CompressionStrategy> = {
      [ContextType.SYSTEM]: CompressionStrategy.SEMANTIC,
      [ContextType.USER]: CompressionStrategy.TRUNCATE,
      [ContextType.HISTORY]: CompressionStrategy.SUMMARY,
      [ContextType.KNOWLEDGE]: CompressionStrategy.SUMMARY,
      [ContextType.WORKFLOW]: CompressionStrategy.SEMANTIC,
      [ContextType.TOOL]: CompressionStrategy.SUMMARY,
      [ContextType.STATE]: CompressionStrategy.SEMANTIC,
      [ContextType.METADATA]: CompressionStrategy.SEMANTIC
    };
    
    return defaultStrategies[item.type] || this.config.strategy;
  }
  
  /**
   * 计算总大小
   */
  private calculateTotalSize(items: ContextItem[]): number {
    return items.reduce((total, item) => total + (item.size || 0), 0);
  }
}